/**
 * ActivityTimelineItem.js
 * Single activity entry for collaboration timeline
 * Task 11 - Module 5 UI
 */

import React from 'react';
import { Avatar } from 'primereact/avatar';
import { getActivityIcon, formatActivityText, formatTimeAgo } from '../../../services/collaborationService';

const ActivityTimelineItem = ({
    activity,
    isLast = false,
    variant = 'default', // 'default' | 'compact' | 'detailed'
    className = ''
}) => {
    const icon = getActivityIcon(activity.action_type);
    const text = formatActivityText(activity);
    const timeAgo = formatTimeAgo(activity.created_at);

    // Color mapping for activity types
    const colorMap = {
        'created': '#3B82F6',
        'member_invited': '#8B5CF6',
        'member_joined': '#22C55E',
        'member_left': '#6B7280',
        'member_removed': '#EF4444',
        'member_updated': '#3B82F6',
        'status_changed': '#EAB308',
        'milestone_completed': '#22C55E',
        'dispute_raised': '#F97316',
        'completed': '#22C55E',
        'cancelled': '#EF4444',
        'commission_distributed': '#22C55E'
    };

    const iconColor = colorMap[activity.action_type] || '#6B7280';

    // Compact variant for tight spaces
    if (variant === 'compact') {
        return (
            <div className={`flex align-items-center gap-2 py-1 ${className}`}>
                <i
                    className={`pi ${icon}`}
                    style={{ color: iconColor, fontSize: '0.875rem' }}
                />
                <span className="text-sm flex-grow-1">{text}</span>
                <span className="text-xs text-500">{timeAgo}</span>
            </div>
        );
    }

    // Detailed variant with actor avatar
    if (variant === 'detailed') {
        return (
            <div className={`flex gap-3 ${className}`}>
                {/* Timeline connector */}
                <div className="flex flex-column align-items-center">
                    <div
                        className="flex align-items-center justify-content-center border-circle"
                        style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: `${iconColor}20`,
                            border: `2px solid ${iconColor}`
                        }}
                    >
                        <i className={`pi ${icon}`} style={{ color: iconColor }} />
                    </div>
                    {!isLast && (
                        <div
                            className="flex-grow-1"
                            style={{
                                width: '2px',
                                backgroundColor: '#E5E7EB',
                                minHeight: '40px'
                            }}
                        />
                    )}
                </div>

                {/* Content */}
                <div className="flex-grow-1 pb-4">
                    <div className="flex align-items-start gap-2 mb-1">
                        {activity.actor_avatar && (
                            <Avatar
                                image={activity.actor_avatar}
                                label={activity.actor_name?.charAt(0) || 'U'}
                                size="small"
                                shape="circle"
                            />
                        )}
                        <div className="flex-grow-1">
                            <div className="font-medium">{text}</div>
                            <div className="text-sm text-500">{timeAgo}</div>
                        </div>
                    </div>

                    {/* Additional details if available */}
                    {activity.description && (
                        <div className="surface-50 border-round p-2 mt-2 text-sm">
                            {activity.description}
                        </div>
                    )}

                    {activity.old_value && activity.new_value && (
                        <div className="flex gap-2 mt-2 text-sm">
                            <span className="text-500 line-through">{activity.old_value}</span>
                            <i className="pi pi-arrow-right text-500" />
                            <span className="font-medium">{activity.new_value}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Default timeline item
    return (
        <div className={`flex gap-3 ${className}`}>
            {/* Timeline connector */}
            <div className="flex flex-column align-items-center">
                <div
                    className="flex align-items-center justify-content-center border-circle"
                    style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: `${iconColor}15`,
                        border: `2px solid ${iconColor}`
                    }}
                >
                    <i
                        className={`pi ${icon}`}
                        style={{ color: iconColor, fontSize: '0.875rem' }}
                    />
                </div>
                {!isLast && (
                    <div
                        className="flex-grow-1"
                        style={{
                            width: '2px',
                            backgroundColor: '#E5E7EB',
                            minHeight: '24px'
                        }}
                    />
                )}
            </div>

            {/* Content */}
            <div className="flex-grow-1 pb-3">
                <div className="flex justify-content-between align-items-start">
                    <span className="text-sm">{text}</span>
                    <span className="text-xs text-500 white-space-nowrap ml-2">{timeAgo}</span>
                </div>
            </div>
        </div>
    );
};

export default ActivityTimelineItem;
